/**
 * ry-floating — positioning engine wrapper.
 *
 * Single chokepoint between ry-ui components and Floating UI. NO component
 * imports `@floating-ui/dom` directly — only this file. This lets us swap
 * the positioning engine in the future without touching every overlay.
 *
 * Exports:
 *   - attachFloating(reference, floating, options) → cleanup function
 *   - createVirtualAnchor(initial?) → { element, update }
 *
 * Design contract:
 *   - Floating element is `position: absolute` (set by component CSS).
 *   - `autoUpdate` keeps position correct under scroll, resize, layout shift.
 *   - The cleanup function MUST be called on close / disconnect / re-anchor.
 *     The wrapping component is responsible for invoking it via RyElement's
 *     listener-cleanup pattern.
 *   - Virtual anchors track a {x, y} point. Use for cursor (context menu),
 *     caret (autocomplete), or drag-thumb (slider bubble) anchoring.
 *
 * Token mapping for placement-to-data-ry-position (mirrored back onto the
 * floating element so existing CSS hooks like `[data-ry-position="top"]`
 * continue to work after migration):
 *   FUI Placement                         data-ry-position
 *   top, top-start, top-end          →    top
 *   bottom, bottom-start, bottom-end →    bottom
 *   left, left-start, left-end       →    left
 *   right, right-start, right-end    →    right
 */

import {
  computePosition,
  autoUpdate,
  offset as offsetMiddleware,
  flip as flipMiddleware,
  shift as shiftMiddleware,
  arrow as arrowMiddleware,
  size as sizeMiddleware,
  hide as hideMiddleware,
  type Placement,
  type Middleware,
  type ReferenceElement,
  type VirtualElement,
} from '@floating-ui/dom';

export type { Placement, ReferenceElement, VirtualElement };

export interface FloatingOptions {
  /** Initial placement; auto-flip may override. Default 'bottom'. */
  placement?: Placement;
  /** Gap in px between reference and floating. Default 8. */
  offset?: number;
  /** Enable flip middleware (jump to opposite side near viewport edge). Default true. */
  flip?: boolean;
  /** Enable shift middleware (slide along main axis to stay in viewport). Default true. */
  shift?: boolean;
  /** Padding from viewport edge for flip/shift, in px. Default 8. */
  padding?: number;
  /** Match floating element's width to reference's width. Default false. */
  matchWidth?: boolean;
  /** If set, position this element as the arrow (must be a child of `floating`). */
  arrow?: HTMLElement | null;
  /** Apply `visibility: hidden` to floating when reference scrolls out of view. Default false. */
  hideWhenReferenceHidden?: boolean;
  /**
   * Mirror the computed side (top/bottom/left/right) onto `floating` as
   * `data-ry-position` after every reposition. Default true.
   * Components using CSS hooks keyed on this attribute (tooltip, dropdown,
   * select, combobox) keep working after migration.
   */
  reflectPlacementAttribute?: boolean;
  /**
   * Called after every reposition with the computed coords + placement.
   * Use sparingly — components shouldn't override positioning logic, but
   * this is here for components that need to react (e.g. flipping an arrow,
   * triggering an animation on placement change).
   */
  onPosition?: (result: { x: number; y: number; placement: Placement }) => void;
}

/**
 * Anchor `floating` to `reference`. Returns a cleanup function.
 *
 * The cleanup function:
 *   - Stops autoUpdate listeners (scroll, resize, ancestor mutation)
 *   - Does NOT remove the floating element from DOM (caller's responsibility)
 *   - Does NOT clear the `data-ry-position` attribute (caller may want it for fade-out CSS)
 *
 * @param reference  An Element or VirtualElement. Use createVirtualAnchor() for cursor/caret/drag anchoring.
 * @param floating   The element to position. Must have `position: absolute` in CSS.
 * @param options    Behavior tuning. See FloatingOptions.
 * @returns A cleanup function. Idempotent — safe to call multiple times.
 */
export function attachFloating(
  reference: ReferenceElement | VirtualElement,
  floating: HTMLElement,
  options: FloatingOptions = {},
): () => void {
  const {
    placement = 'bottom',
    offset = 8,
    flip = true,
    shift = true,
    padding = 8,
    matchWidth = false,
    arrow = null,
    hideWhenReferenceHidden = false,
    reflectPlacementAttribute = true,
    onPosition,
  } = options;

  const middleware: Middleware[] = [];
  middleware.push(offsetMiddleware(offset));
  if (flip) middleware.push(flipMiddleware({ padding }));
  if (shift) middleware.push(shiftMiddleware({ padding }));
  if (matchWidth) {
    middleware.push(sizeMiddleware({
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        });
      },
    }));
  }
  if (arrow) middleware.push(arrowMiddleware({ element: arrow }));
  if (hideWhenReferenceHidden) middleware.push(hideMiddleware());

  let disposed = false;

  const update = async (): Promise<void> => {
    if (disposed) return;
    const { x, y, placement: computedPlacement, middlewareData } =
      await computePosition(reference, floating, { placement, middleware, strategy: 'absolute' });

    Object.assign(floating.style, {
      left: `${x}px`,
      top: `${y}px`,
    });

    if (reflectPlacementAttribute) {
      const side = computedPlacement.split('-')[0]!; // 'top' | 'bottom' | 'left' | 'right'
      floating.dataset.ryPosition = side;
    }

    if (arrow && middlewareData.arrow) {
      const { x: ax, y: ay } = middlewareData.arrow;
      Object.assign(arrow.style, {
        left: ax != null ? `${ax}px` : '',
        top: ay != null ? `${ay}px` : '',
      });
    }

    if (hideWhenReferenceHidden && middlewareData.hide) {
      floating.style.visibility = middlewareData.hide.referenceHidden ? 'hidden' : '';
    }

    onPosition?.({ x, y, placement: computedPlacement });
  };

  // autoUpdate listens to scroll + resize + layout shifts and re-invokes our
  // update fn. Returns its own cleanup which we wrap.
  const stopAutoUpdate = autoUpdate(reference as Element, floating, update);

  return () => {
    if (disposed) return;
    disposed = true;
    stopAutoUpdate();
  };
}

/**
 * A virtual reference element that tracks a {x, y} point in viewport
 * coordinates. Use as the `reference` argument to `attachFloating` for:
 *
 *   - Context menus (anchor to {clientX, clientY} from contextmenu event)
 *   - Autocomplete (anchor to caret position in a focused input)
 *   - Slider value bubble (anchor to drag thumb position)
 *
 * Call `update({ x, y, width?, height? })` to re-anchor. The next autoUpdate
 * tick will reposition the floating element.
 *
 * If your virtual position derives from a real element (e.g. a drag thumb),
 * pass that element via `contextElement` so Floating UI can correctly resolve
 * clipping ancestors and trigger reposition when that element scrolls.
 *
 * @param initial         Starting position. Defaults to {x:0, y:0, width:0, height:0}.
 * @param contextElement  Optional real element for clipping/scroll-tracking.
 */
export function createVirtualAnchor(
  initial: { x: number; y: number; width?: number; height?: number } = { x: 0, y: 0 },
  contextElement?: Element,
): {
  element: VirtualElement;
  update: (rect: { x: number; y: number; width?: number; height?: number }) => void;
} {
  let x = initial.x;
  let y = initial.y;
  let width = initial.width ?? 0;
  let height = initial.height ?? 0;

  const element: VirtualElement = {
    getBoundingClientRect: () => ({
      x, y,
      top: y,
      left: x,
      right: x + width,
      bottom: y + height,
      width,
      height,
      toJSON: () => ({ x, y, top: y, left: x, right: x + width, bottom: y + height, width, height }),
    }),
    ...(contextElement ? { contextElement } : {}),
  };

  return {
    element,
    update(rect) {
      x = rect.x;
      y = rect.y;
      width = rect.width ?? 0;
      height = rect.height ?? 0;
    },
  };
}
